import { useCallback, useState } from "react";
import { useNotifyStatus } from "contexts/status";
import {
  GeminiDeliveryFormTool,
  DeliveryFormSave,
  DeliveryFormRead,
  useProcessAudioWithGeminiMutation,
} from "src/store/api/generatedApi";
import { useTenant } from "hooks/settings";

interface UseGeminiAIOptions {
  onFieldUpdate: (
    field: keyof DeliveryFormSave,
    value: string | number
  ) => void;
  currentFormData: DeliveryFormSave;
  onSaveAndNewDelivery?: () => Promise<void>;
  onOpenPreviousDelivery?: (id: string) => Promise<void>;
  savedDeliveries?: DeliveryFormRead[];
}

interface UseGeminiAIReturn {
  processAudioClip: (audioBlob: Blob) => Promise<void>;
  isProcessing: boolean;
}

export const useGeminiAI = ({
  onFieldUpdate,
  currentFormData,
  onSaveAndNewDelivery,
  onOpenPreviousDelivery,
  savedDeliveries = [],
}: UseGeminiAIOptions): UseGeminiAIReturn => {
  const [isProcessing, setIsProcessing] = useState(false);
  const notifyStatus = useNotifyStatus();
  const tenantName = useTenant();
  const [processAudioWithGemini] = useProcessAudioWithGeminiMutation();

  const processAudioClip = useCallback(
    async (audioBlob: Blob) => {
      setIsProcessing(true);

      try {
        // Convert audio blob to base64
        const audioBase64 = await blobToBase64(audioBlob);

        // Define available tools based on the current context
        const availableTools = [
          "update_bay_number",
          "update_commodity",
          "update_weight",
          "update_deviations",
          "update_notes",
          "update_truck_number",
          "update_name",
          "save_and_new_delivery",
        ] as GeminiDeliveryFormTool[];

        // Add delivery form tools if we have saved deliveries
        if (savedDeliveries.length > 0) {
          availableTools.push(
            "open_delivery_form",
            "go_to_completed_deliveries"
          );
        }

        // Call the backend endpoint using RTK Query
        const result = await processAudioWithGemini({
          tenantName,
          processAudioWithGeminiRequest: {
            audio_data: audioBase64,
            available_tools: availableTools,
            current_form_data: currentFormData,
          },
        }).unwrap();

        if (!result.success) {
          throw new Error(result.error || "Unknown error processing audio");
        }

        // Process function calls from the response
        if (result.function_calls) {
          result.function_calls.forEach((call: any) => {
            switch (call.name as string) {
              case "update_bay_number":
                notifyStatus({
                  type: "success",
                  text:
                    "Okay, I am updating the bay number to " +
                    call.parameters.value,
                });
                onFieldUpdate("bay", call.parameters.value);
                break;
              case "update_commodity":
                notifyStatus({
                  type: "success",
                  text:
                    "Okay, I am updating the commodity to " +
                    call.parameters.value,
                });
                onFieldUpdate("commodity", call.parameters.value);
                break;
              case "update_weight":
                notifyStatus({
                  type: "success",
                  text:
                    "Okay, I am updating the weight to " +
                    call.parameters.value,
                });
                onFieldUpdate("mass_lbs", call.parameters.value);
                break;
              case "update_deviations":
                notifyStatus({
                  type: "success",
                  text:
                    "Okay, I am updating the deviations to " +
                    call.parameters.value,
                });
                onFieldUpdate("deviations", call.parameters.value);
                break;
              case "update_notes":
                notifyStatus({
                  type: "success",
                  text:
                    "Okay, I am updating the notes to " + call.parameters.value,
                });
                onFieldUpdate("notes", call.parameters.value);
                break;
              case "update_truck_number":
                notifyStatus({
                  type: "success",
                  text:
                    "Okay, I am updating the truck number to " +
                    call.parameters.value,
                });
                onFieldUpdate("truck_number", call.parameters.value);
                break;
              case "update_name":
                notifyStatus({
                  type: "success",
                  text:
                    "Okay, I am naming this delivery: " + call.parameters.value,
                });
                onFieldUpdate("name", call.parameters.value);
                break;
              case "save_and_new_delivery":
                if (onSaveAndNewDelivery) {
                  notifyStatus({
                    type: "success",
                    text: "Okay, I am saving the delivery and starting a new one",
                  });
                  onSaveAndNewDelivery().catch(console.error);
                }
                break;
              case "open_delivery_form":
                if (onOpenPreviousDelivery && call.parameters.deliveryId) {
                  notifyStatus({
                    type: "success",
                    text:
                      "Okay, I am opening the previous delivery form with ID " +
                      call.parameters.deliveryId,
                  });
                  onOpenPreviousDelivery(call.parameters.deliveryId).catch(
                    console.error
                  );
                }
                break;
              case "go_to_completed_deliveries":
                notifyStatus({
                  type: "success",
                  text: "Okay, I am going to the completed deliveries page",
                });
                break;
            }
          });
        }
      } catch (error) {
        console.error("Error processing audio with Gemini:", error);
        notifyStatus({
          type: "error",
          text: "Error processing audio: " + (error as Error).message,
        });
      } finally {
        setIsProcessing(false);
      }
    },
    [
      onFieldUpdate,
      currentFormData,
      savedDeliveries,
      onSaveAndNewDelivery,
      onOpenPreviousDelivery,
      notifyStatus,
      tenantName,
      processAudioWithGemini,
    ]
  );

  return {
    processAudioClip,
    isProcessing,
  };
};

// Helper function to convert blob to base64
async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(",")[1]; // Remove data URL prefix
      resolve(base64 || "");
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}
